import showError from '#helpers/alertMessages/showError.js'
import getHeaders from '#helpers/getHeaders.js'
import httpStatus from '#helpers/httpStatus.js'
import i18n from '#helpers/i18n/i18n.js'
import Logger from '#helpers/logging/Logger.js'
import { showModal } from '#helpers/modals.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { create } from 'axios'
import FileSaver from 'file-saver'
import { defineAsyncComponent } from 'vue'

const SessionExpiredModal = defineAsyncComponent(
	() => import('#modules/auth/components/SessionExpiredModal.vue'),
)

const axiosHttpClient = create({ baseURL: `${import.meta.env.VITE_API_URL}/api/v1` })

const getFilename = header => {
	if (!header) return null

	// 1. Try to find filename* (RFC 5987) — ';' only appears encoded (%3B) in the value
	const filenameStar = header.match(/filename\*=([^;']+)'[^']*'([^;]+)/)
	if (filenameStar) {
		return decodeURIComponent(filenameStar[2])
	}

	// 2. Quoted filename — semicolons and spaces are legal inside the quotes
	const quoted = header.match(/filename="([^"]+)"/)
	if (quoted) {
		return quoted[1]
	}

	// 3. Last resort: unquoted token, ends at ';' or the end of the header
	const unquoted = header.match(/filename=([^;]+)/)
	return unquoted ? unquoted[1].trim() : null
}

/**
 * Helper for axios to download files.
 * @param {object} cfg - Configuration
 * @param {string} [cfg.method] - The method for the action
 * @param {string} [cfg.url] - The URL for the action
 * @param {object} [cfg.data] - The data for the action
 * @param {object} [cfg.config] - The config to pass through to axios
 * @param {string|null} [cfg.fileName] - The filename to download the file with
 * @returns {Promise} - A promise with a file
 */
axiosHttpClient.getFile = ({
	method = 'GET',
	url = 'does-not-exist',
	data = {},
	config = { responseType: 'arraybuffer' },
	fileName = null,
} = {}) => {
	return new Promise((resolve, reject) =>
		axiosHttpClient
			.request({ method, url, data, ...config })
			.then(response => {
				if (response?.status === httpStatus.OK) {
					const possibleFileNames = []
					possibleFileNames.push(fileName)
					possibleFileNames.push(response?.headers?.['x-content-filename'])

					const disposition = response.headers['content-disposition']

					if (disposition) {
						possibleFileNames.push(getFilename(disposition))
					}

					possibleFileNames.push('filename.extension')
					const dynamicFileName = possibleFileNames.filter(Boolean)[0]
					FileSaver.saveAs(new File([response.data], dynamicFileName))
					resolve()
				} else {
					reject('File status is not OK')
				}
			})
			.catch(e => reject(e)),
	)
}

axiosHttpClient.postFile = (url, file) => {
	return new Promise((resolve, reject) => {
		const fd = new FormData()
		fd.append('file', file)
		axiosHttpClient
			.post(url, fd)
			.then(response => {
				if ([httpStatus.OK, httpStatus.CREATED].includes(response?.status)) {
					resolve(response)
				} else {
					reject('File status is not OK')
				}
			})
			.catch(e => reject(e))
	})
}

// inject token headers into every request
axiosHttpClient.interceptors.request.use(config => {
	const auth = useAuthorizationStore()
	const headers = getHeaders(auth.accessToken)
	config.headers = { ...headers, ...config.headers }
	return config
})

let sessionOverFlag = false
// intercept every response to check for fails
axiosHttpClient.interceptors.response.use(
	function (response) {
		// Any status code that lie within the range of 2xx cause this function to trigger
		// Do something with response data
		return response
	},
	async function (error) {
		Logger.debug('axiosHttpClient:onRejected')
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		// Do something with response error

		// API is overloaded
		if (error?.response?.status === httpStatus.TOO_MANY_REQUESTS) {
			await showError(i18n.global.t('common.tooManyRequests'))
		}

		// The user is (no longer) authorized
		if (error?.response?.status === httpStatus.UNAUTHORIZED) {
			Logger.debug('axiosHttpClient intercepted an unauthorized response')

			// Determine whether the failed URL is an auth route (login/logout)
			const responseURL = error?.request?.responseURL
			const requestURL = error?.config?.url
			const urlToCheck = responseURL || requestURL
			// Normalize to pathname to avoid mismatches with domain, query, or hash
			let pathname = ''
			try {
				pathname = new URL(urlToCheck, 'http://local.base').pathname || ''
			} catch {
				pathname = urlToCheck
			}
			const isAuthRoute =
				pathname.endsWith('/auth/login') ||
				pathname.endsWith('/auth/logout') ||
				pathname === 'auth/login' ||
				pathname === 'auth/logout'

			if (!isAuthRoute) {
				if (!sessionOverFlag) {
					sessionOverFlag = true
					const auth = useAuthorizationStore()
					await auth.logout()
					await showModal(SessionExpiredModal)
					sessionOverFlag = false
					return Promise.reject()
				}
				return Promise.reject()
			}
		}
		return Promise.reject(error)
	},
)

export default axiosHttpClient
